/**
 * The studio checkout and the server's price (fxrybackend helper/priceOrder.js
 * and helper/pricingEnforce.js; docs/payment-trust-spec.md, Decisions
 * 2026-10-03).
 *
 * The backend says whether it enforces its own price (GET order/quote ->
 * { mode }). Only when mode is "on" does this checkout show the server's
 * quote (POST order/quote) instead of adding prices up itself. With "off" or
 * "shadow" the checkout works exactly as it always has.
 *
 * Staff overrides (a custom line, rush, a waived pick-up or ship-back fee)
 * each need a short reason. The server saves it on the order with who took
 * it, and refuses an override with no reason once enforcement is on.
 *
 * Pure: no React, no axios, so node:test can load it.
 */

export const isServerPricingOn = (mode) =>
  String(mode || "")
    .trim()
    .toLowerCase() === "on";

const dollars = (cents) => Math.round(Number(cents) || 0) / 100;

/** The studio ship-back fee from Settings (`ship_back_fee`, $25 by
 *  default). Never the online `shipping_fee`: the studio charges more on
 *  purpose. null when Settings didn't say, so checkout can wait for it. */
export const shipBackFeeFrom = (settings) => {
  const fee = Number(settings?.ship_back_fee);
  return Number.isFinite(fee) && fee >= 0 ? fee : null;
};

/** A stable key for a custom line's reason box. */
export const customLineKey = (service, index) =>
  `${index}:${service?.item_details?.id ?? ""}:${service?.name ?? ""}`;

export const customLines = (services = []) =>
  (services || [])
    .map((service, index) => ({ service, index }))
    .filter(({ service }) => service?.is_custom === true);

const filled = (text) => String(text ?? "").trim() !== "";

/** The custom lines staff still have to give a reason for. A line that
 *  arrives with its own reason doesn't ask again: the sweater screen's
 *  custom work and restoration price are typed with what the work is, so
 *  checkout asking "why this price?" was the same question twice
 *  (Rebecca, 2026-10-06). */
export const linesNeedingReason = (services = []) =>
  customLines(services).filter(
    ({ service }) => !filled(service?.override_reason),
  );

/**
 * "On us" (fxrybackend helper/onUs.js): staff give a line away. `onUs` maps
 * a line's customLineKey to { on, reason }. The line is sent at $0 with what
 * it would have cost; the server checks that price against the price list
 * and records who gave it away.
 */
export const onUsEntry = (onUs, service, index) => {
  const entry = onUs?.[customLineKey(service, index)];
  return entry?.on === true ? entry : null;
};

/** The cart's services with every on-us line at $0. */
export const withOnUs = (services = [], onUs = {}, itemsData = []) =>
  (services || []).map((service, index) => {
    const entry = onUsEntry(onUs, service, index);
    if (!entry) return service;
    const garment = (itemsData || []).find(
      (item) => item?.id === service?.item_details?.id,
    );
    const listPrice =
      garment?.pricing_plan === "Standard"
        ? service?.standard_price
        : service?.premium_price;
    return {
      ...service,
      standard_price: "0",
      premium_price: "0",
      on_us: true,
      on_us_reason: String(entry.reason ?? "").trim() || null,
      on_us_list_price: String(listPrice ?? ""),
    };
  });

/** What still needs a reason before Pay. Empty when nothing does. */
export const missingReasons = ({
  services = [],
  customReasons = {},
  onUs = {},
  waiveShipBack = false,
  waivePickup = false,
  feeWaiverReason = "",
} = {}) => {
  const missing = [];
  for (const { service, index } of linesNeedingReason(services)) {
    if (!filled(customReasons[customLineKey(service, index)])) {
      missing.push(
        `Add a reason for the custom price on "${service?.name || "custom item"}".`,
      );
    }
  }
  (services || []).forEach((service, index) => {
    const entry = onUsEntry(onUs, service, index);
    if (entry && !filled(entry.reason)) {
      missing.push(`Say why "${service?.name || "this item"}" is on us.`);
    }
  });
  if ((waiveShipBack || waivePickup) && !filled(feeWaiverReason)) {
    missing.push("Add a reason for waiving the fee.");
  }
  return missing;
};

/** The cart's services with each custom line's reason on it. */
export const withCustomReasons = (services = [], customReasons = {}) =>
  (services || []).map((service, index) =>
    service?.is_custom === true
      ? {
          ...service,
          override_reason:
            String(customReasons[customLineKey(service, index)] ?? "").trim() ||
            String(service?.override_reason ?? "").trim() ||
            null,
        }
      : service,
  );

/** The reasons that travel at the top of the order payload. */
export const reasonFields = ({
  waiveShipBack = false,
  waivePickup = false,
  feeWaiverReason = "",
} = {}) => ({
  ...((waiveShipBack || waivePickup) && filled(feeWaiverReason)
    ? { fee_waiver_reason: String(feeWaiverReason).trim() }
    : {}),
});

/** The fees this checkout sends. A waived fee is sent as 0: that is how the
 *  server knows it was waived (and asks for the reason). */
export const popupDeliveryFees = ({
  wantsMailBack = false,
  shipBackFee = 0,
  waiveShipBack = false,
  deliveryType = "boutique",
  pickupFee = 0,
  waivePickup = false,
} = {}) => ({
  inbound: 0,
  return: wantsMailBack && !waiveShipBack ? Number(shipBackFee) || 0 : 0,
  pickup:
    deliveryType !== "boutique" && !waivePickup ? Number(pickupFee) || 0 : 0,
});

/** What POST order/quote is asked about: the same fields the checkout
 *  sends with the payment. The gift certificate is left out: it is a way
 *  of paying, and comes off the server's total afterwards. */
export const quotePayload = ({
  cart = {},
  services,
  discountCode = "",
  deliveryType = "boutique",
  wantsMailBack = false,
  deliveryFees = { inbound: 0, return: 0, pickup: 0 },
  rushFee = 0,
  reasons = {},
  email = "",
  intakePerson = "",
} = {}) => ({
  itemsData: cart?.itemsData || [],
  services: services || cart?.services || [],
  type: "Popup",
  discountCode: String(discountCode || "").toUpperCase(),
  deliveryType,
  return_delivery_method: wantsMailBack ? "ship" : "studio-pickup",
  delivery_fees: deliveryFees,
  rush_fee: Number(rushFee) || 0,
  ...reasons,
  intake_person: intakePerson || null,
  user: { personalInfo: { email: email || "" } },
});

/** The server's quote as the numbers the summary shows, in dollars. */
export const displayFromQuote = (quote) => {
  if (!quote) return null;
  const discount = quote.discount || {};
  const fees = quote.fees || {};
  return {
    linePrices: (quote.lines || []).map((l) => dollars(l.server_cents)),
    servicesSubtotal: dollars(quote.services_cents),
    pickupFee: dollars(fees.pickup?.server_cents),
    pickupWaived: Boolean(fees.pickup?.waived),
    shipBackFee: dollars(fees.return?.server_cents),
    shipBackWaived: Boolean(fees.return?.waived),
    rushFee: dollars(quote.rush_cents),
    discount: dollars(discount.server_cents),
    discountCode: discount.code || null,
    discountOk: discount.ok === true,
    discountNote:
      discount.code && discount.ok !== true
        ? `${discount.code} can't be used on this order${
            discount.reason ? ` (${discount.reason})` : ""
          }.`
        : null,
    tax: dollars(quote.tax_cents),
    total: dollars(quote.total_cents),
    totalCents: Math.round(Number(quote.total_cents) || 0),
    preTax: dollars(quote.discounted_cents),
  };
};

/** A refusal from the payment or order routes, or null if it isn't one. */
export const pricingRefusal = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object" || typeof data.code !== "string") {
    return null;
  }
  const known = [
    "prices_changed",
    "staff_login_needed",
    "override_reason_needed",
    "cant_price",
    "custom_line_online",
    "rush_online",
    "no_order_payload",
  ];
  if (!known.includes(data.code)) return null;
  return {
    code: data.code,
    status: error?.response?.status ?? null,
    message: data.message || data.error || "Prices updated, please review.",
    quote: data.quote || null,
  };
};

const money = (cents) =>
  `$${(Math.round(Number(cents) || 0) / 100).toFixed(2)}`;

/** The notice shown when the server's total differs from the one shown. */
export const pricesChangedNotice = (quote) =>
  quote && Number.isFinite(Number(quote.total_cents))
    ? `Prices updated, please review. The total is now ${money(quote.total_cents)}.`
    : "Prices updated, please review.";
